import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, MapPin, Users, Maximize, Bed, Wifi, Tv, Wind, Coffee, Wine } from 'lucide-react';
import { useBooking, Room } from '../hooks/useBooking';
interface RoomSelectionProps {
  onSelectRoom: () => void;
  onBack: () => void;
}
const getRoomAmenities = (amenities: string[]) => {
  return amenities.map(amenity => {
    let icon = null;
    switch (amenity) {
      case 'WiFi':
        icon = <Wifi className="w-4 h-4" data-id="element-365" />;
        break;
      case 'AC':
        icon = <Wind className="w-4 h-4" data-id="element-366" />;
        break;
      case 'TV':
        icon = <Tv className="w-4 h-4" data-id="element-367" />;
        break;
      case 'Minibar':
        icon = <Wine className="w-4 h-4" data-id="element-368" />;
        break;
      case 'Room Service':
        icon = <Coffee className="w-4 h-4" data-id="element-369" />;
        break;
      default:
        icon = <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-370" />;
    }
    return <div key={amenity} className="flex items-center text-sm text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded-lg" data-id="element-371">
        {icon}
        <span className="ml-2" data-id="element-372">{amenity}</span>
      </div>;
  });
};
export function RoomSelection({
  onSelectRoom,
  onBack
}: RoomSelectionProps) {
  const {
    selectedHotel,
    selectRoom
  } = useBooking();
  if (!selectedHotel) {
    return <div className="pt-32 pb-20 text-center min-h-screen bg-luxury-cream" data-id="element-373">
        <p data-id="element-374">No hotel selected.</p>
        <button onClick={onBack} className="mt-4 text-luxury-blue underline" data-id="element-375">
          Go back
        </button>
      </div>;
  }
  // Generate mock rooms based on the selected hotel
  const MOCK_ROOMS: Room[] = [{
    id: 'r1',
    name: 'Garden Room',
    priceModifier: 0,
    size: 28,
    maxGuests: 2,
    bedType: 'Queen bed',
    amenities: ['WiFi', 'AC', 'TV'],
    image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'r2',
    name: 'Pool View Suite',
    priceModifier: 50,
    size: 35,
    maxGuests: 2,
    bedType: 'King bed',
    amenities: ['WiFi', 'AC', 'TV', 'Minibar'],
    image: 'https://images.unsplash.com/photo-1602002418816-5c0aeef426aa?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'r3',
    name: 'Jungle Villa',
    priceModifier: 120,
    size: 52,
    maxGuests: 3,
    bedType: 'King bed + Daybed',
    amenities: ['WiFi', 'AC', 'TV', 'Minibar', 'Room Service'],
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'r4',
    name: 'Royal Pavilion',
    priceModifier: 280,
    size: 85,
    maxGuests: 4,
    bedType: '2 King beds',
    amenities: ['WiFi', 'AC', 'TV', 'Minibar', 'Room Service', 'Butler Service'],
    image: 'https://images.unsplash.com/photo-1559628233-100c798642d4?auto=format&fit=crop&w=800&q=80'
  }];
  const handleSelectRoom = (room: Room) => {
    selectRoom(room);
    onSelectRoom();
  };
  return <div className="pt-24 pb-20 min-h-screen bg-luxury-cream" data-id="element-376">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-377">
        {/* Back button & Breadcrumb */}
        <button onClick={onBack} className="flex items-center text-gray-500 hover:text-luxury-blue transition-colors mb-6 font-medium text-sm" data-id="element-378">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-379" />
          Back to results
        </button>

        {/* Hotel Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mb-10" data-id="element-380">
          <div className="h-64 md:h-80 relative" data-id="element-381">
            <img src={selectedHotel.image} alt={selectedHotel.name} className="w-full h-full object-cover" data-id="element-382" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-383" />
            <div className="absolute bottom-0 left-0 p-6 md:p-8" data-id="element-384">
              <div className="flex items-center space-x-1 mb-2" data-id="element-385">
                {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.floor(selectedHotel.rating) ? 'fill-luxury-gold text-luxury-gold' : 'text-white/30'}`} data-id="element-386" />)}
              </div>
              <h1 className="text-3xl md:text-5xl font-italiana text-white mb-2" data-id="element-387">
                {selectedHotel.name}
              </h1>
              <p className="text-white/90 flex items-center" data-id="element-388">
                <MapPin className="w-4 h-4 mr-1.5" data-id="element-389" /> {selectedHotel.location}
              </p>
            </div>
          </div>
        </motion.div>

        <h2 className="text-3xl font-italiana text-luxury-slate mb-6" data-id="element-390">
          Select Your Room
        </h2>

        {/* Room List */}
        <div className="space-y-6" data-id="element-391">
          {MOCK_ROOMS.map((room, index) => <motion.div key={room.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-float transition-shadow duration-300 border border-gray-100 flex flex-col md:flex-row" data-id="element-392">
              {/* Room Image */}
              <div className="w-full md:w-80 h-56 md:h-auto shrink-0 relative" data-id="element-393">
                <img src={room.image} alt={room.name} className="w-full h-full object-cover" data-id="element-394" />
              </div>

              {/* Room Details */}
              <div className="p-6 flex flex-col flex-1" data-id="element-395">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4" data-id="element-396">
                  <div data-id="element-397">
                    <h3 className="text-2xl font-italiana font-bold text-luxury-slate mb-3" data-id="element-398">
                      {room.name}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4" data-id="element-399">
                      <div className="flex items-center" data-id="element-400">
                        <Maximize className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-401" />{' '}
                        {room.size} sqm
                      </div>
                      <div className="flex items-center" data-id="element-402">
                        <Users className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-403" /> Up to{' '}
                        {room.maxGuests} guests
                      </div>
                      <div className="flex items-center" data-id="element-404">
                        <Bed className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-405" />{' '}
                        {room.bedType}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 md:mt-0 md:text-right bg-gray-50 md:bg-transparent p-4 md:p-0 rounded-xl md:rounded-none flex md:block justify-between items-center" data-id="element-406">
                    <div data-id="element-407">
                      <div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 hidden md:block" data-id="element-408">
                        Price
                      </div>
                      <div className="text-3xl font-bold text-luxury-slate" data-id="element-409">
                        £{selectedHotel.price + room.priceModifier}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-410">per night</div>
                    </div>
                    <button onClick={() => handleSelectRoom(room)} className="md:hidden bg-luxury-blue hover:bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-411">
                      Select
                    </button>
                  </div>
                </div>

                <div className="mt-auto" data-id="element-412">
                  <div className="text-xs font-semibold text-gray-800 uppercase tracking-wider mb-2" data-id="element-413">
                    Room Amenities
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6 md:mb-0" data-id="element-414">
                    {getRoomAmenities(room.amenities)}
                  </div>
                </div>

                <div className="hidden md:flex justify-end mt-4" data-id="element-415">
                  <button onClick={() => handleSelectRoom(room)} className="bg-luxury-blue hover:bg-blue-600 text-white px-8 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-416">
                    Book This Room
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}